import React from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { ArrowRight } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";

export const SolutionDialog = ({ solution, onClose }) => {
  const { t, path } = useLang();
  const navigate = useNavigate();
  if (!solution) return null;
  const data = t.solutionsData[solution.id];

  return (
    <Dialog open={!!solution} onOpenChange={(o) => !o && onClose()}>
      <DialogContent closeLabel={t.a11y.closeMenu} className="max-w-2xl bg-white border-black/10 p-0 gap-0 overflow-hidden max-h-[90vh] overflow-y-auto" data-testid="solution-dialog">
        <div className="relative h-48">
          <img src={solution.image} alt={data.name} className="w-full h-full object-cover" />
          <span className="absolute top-4 left-4 text-[10px] tracking-[0.15em] uppercase bg-[#061A26]/70 backdrop-blur-sm text-white/90 px-3 py-1.5">
            {t.solutions.demoBadge}
          </span>
        </div>
        <div className="p-8 md:p-10">
          <DialogHeader className="text-left space-y-0">
            <DialogTitle className="text-2xl font-semibold tracking-tight text-[#061A26]" data-testid="solution-dialog-title">
              {data.name}
            </DialogTitle>
            <DialogDescription className="pt-3 text-sm text-slate-600 leading-relaxed">
              {data.desc}
            </DialogDescription>
          </DialogHeader>

          <div className="mt-7">
            <h4 className="text-xs font-semibold tracking-[0.2em] uppercase text-slate-500">{t.solutions.possibilitiesLabel}</h4>
            <ul className="mt-3 space-y-2">
              {data.possibilities.map((p) => (
                <li key={p} className="flex items-center gap-3 text-sm text-[#061A26]">
                  <span className="w-4 h-px bg-[#19C7C1]" />
                  {p}
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 text-sm border-t border-black/5 pt-6">
            <div>
              <p className="text-xs font-semibold tracking-[0.15em] uppercase text-slate-500">{t.solutions.positioningLabel}</p>
              <p className="mt-1.5 text-slate-700 leading-relaxed">{data.positioning}</p>
            </div>
            <div>
              <p className="text-xs font-semibold tracking-[0.15em] uppercase text-slate-500">{t.solutions.availabilityLabel}</p>
              <p className="mt-1.5 text-slate-700">{data.availability}</p>
            </div>
            <div>
              <p className="text-xs font-semibold tracking-[0.15em] uppercase text-slate-500">{t.solutions.targetLabel}</p>
              <p className="mt-1.5 text-slate-700">{data.targetMarket}</p>
            </div>
            <div>
              <p className="text-xs font-semibold tracking-[0.15em] uppercase text-slate-500">{t.solutions.docLabel}</p>
              <p className="mt-1.5 text-slate-700">{data.docStatus}</p>
            </div>
          </div>

          <div className="mt-7 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between border-t border-black/5 pt-6">
            <p className="text-xs text-slate-500">
              {t.solutions.sheetLabel}: {solution.sheetUrl ? (
                <a href={solution.sheetUrl} className="text-[#19C7C1] underline" target="_blank" rel="noopener noreferrer">PDF</a>
              ) : t.solutions.sheetOnRequest}
            </p>
            <button
              onClick={() => { onClose(); navigate(path(`/contact?interest=product&topic=${solution.id}`)); }}
              className="btn-primary text-sm"
              data-testid="solution-dialog-inquiry-btn"
            >
              {t.solutions.requestInfo}
              <ArrowRight size={15} />
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
